"use client";

import type { LucideIcon } from "lucide-react";
import {
  Calendar,
  FolderKanban,
  IndianRupee,
  Users,
} from "lucide-react";
import { AnimatedCounter } from "@/components/motion/AnimatedCounter";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import type { Stats } from "@/lib/api-types";
import { cn } from "@/lib/utils";

const formatInr = (value: number) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value);

interface HeroStatDisplay {
  label: string;
  icon: LucideIcon;
  prefix?: string;
  suffix?: string;
  value: number;
}

function buildHeroStats(stats?: Stats): HeroStatDisplay[] {
  const raised = stats?.raisedThisYear ?? 1_250_000;

  return [
    {
      label: "Raised this year",
      icon: IndianRupee,
      prefix: "₹",
      value: raised,
    },
    {
      label: "Lives impacted",
      icon: Users,
      value: stats?.livesImpacted ?? 50_000,
      suffix: "+",
    },
    {
      label: "Projects completed",
      icon: FolderKanban,
      value: stats?.projectsCompleted ?? 10,
      suffix: "+",
    },
    {
      label: "Years of service",
      icon: Calendar,
      value: stats?.yearsOfService ?? 4,
      suffix: "+",
    },
  ];
}

function formatStatValue(stat: HeroStatDisplay, n: number) {
  const formatted = formatInr(n);
  return `${stat.prefix ?? ""}${formatted}${stat.suffix ?? ""}`;
}

interface HeroStatsCardsProps {
  stats?: Stats;
}

export function HeroStatsCards({ stats }: HeroStatsCardsProps) {
  const items = buildHeroStats(stats);

  return (
    <div id="home-stats" className="hero-stats-bar w-full">
      <Stagger className="hero-stats-bar-inner grid grid-cols-2 md:grid-cols-4">
        {items.map((stat, index) => {
          const Icon = stat.icon;

          return (
            <StaggerItem key={stat.label}>
              <div
                className={cn(
                  "hero-stats-bar-cell flex items-center gap-3",
                  index > 0 && "md:border-l md:border-white/15"
                )}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-[var(--charity-gold)]">
                  <Icon size={18} aria-hidden />
                </span>
                <div className="min-w-0 text-left">
                  <p className="break-words text-sm font-bold leading-tight text-[var(--charity-gold)] sm:text-base md:text-lg">
                    <AnimatedCounter
                      value={stat.value}
                      formatter={(n) => formatStatValue(stat, n)}
                    />
                  </p>
                  <p className="mt-0.5 text-xs leading-tight text-white/85">
                    {stat.label}
                  </p>
                </div>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  );
}
